import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { ApiErrorCode, DocumentAcceptance } from 'src/app/interfaces';
import { AcceptLegalDialogComponent } from 'src/app/shared/accept-legal-dialog/accept-legal-dialog.component';
import { ConsentService } from './consent.service';
import { SpinnerOverlayGuardService } from './spinner-overlay-guard.service';

/** Fullscreen and undismissable: the gate is answered, never closed. */
const DIALOG_CONFIG = {
  width: '100vw',
  maxWidth: '100vw',
  height: '100vh',
  maxHeight: '100vh',
  disableClose: true,
  autoFocus: false,
};

/**
 * Decides when the blocking acceptance dialog is on screen, and keeps exactly one of them.
 *
 * A dialog rather than a route so the destination survives being gated: a routed gate swallows the
 * private URL and lands the user on `/home`, costing a scanned QR code its store.
 *
 * Presented on every `NavigationEnd`, so a version published mid-session is picked up at the next
 * navigation rather than the next full page load. `ConsentService.load` is idempotent, so this
 * costs a request once.
 *
 * Its own service rather than logic on `ConsentService`: that one is data access, and a data
 * service opening a component would invert the dependency direction.
 *
 * It also sends the acceptances the dialog collected. The dialog closes on the tick rather than on
 * the response, so by the time the server answers there is no dialog left to handle it.
 */
@Injectable({ providedIn: 'root' })
export class AcceptLegalGateService {
  private readonly dialog = inject(MatDialog);
  private readonly consent = inject(ConsentService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly spinnerGuard = inject(SpinnerOverlayGuardService);

  /** The open dialog, so a second navigation cannot stack another on top of it. */
  private dialogRef: MatDialogRef<AcceptLegalDialogComponent, DocumentAcceptance[] | null> | null =
    null;
  private watching = false;

  /** Start gating. Called by the private shell once it mounts; safe to call more than once. */
  public async start(): Promise<void> {
    this.watchNavigation();
    await this.consent.whenResolved();
    this.present();
  }

  /**
   * Present the gate after the server has refused a request for want of consent. Forces a re-read
   * first: reaching here means the server contradicted the client's state, and the stale answer is
   * the one that would decide nothing is owed.
   *
   * False when the refreshed state still reports nothing pending — `/consents/me` is unreachable
   * and `ConsentService` fails open, so there is no gate to show and the caller must say something
   * else.
   */
  public async presentAfterServerRefusal(): Promise<boolean> {
    await this.consent.load(true);
    if (!this.consent.hasPendingDocuments()) return false;
    this.present();
    return true;
  }

  private present(): void {
    if (this.dialogRef || !this.consent.hasPendingDocuments()) return;
    // This opens off a router event, so it cannot know whether a page left a spinner up on its way
    // here — and an undismissable dialog under one has no way out. See `SpinnerOverlayGuardService`.
    this.spinnerGuard.dismissAll();
    this.dialogRef = this.dialog.open(AcceptLegalDialogComponent, DIALOG_CONFIG);
    this.dialogRef.afterClosed().subscribe(acceptances => {
      this.dialogRef = null;
      if (acceptances?.length) void this.record(acceptances);
    });
  }

  /**
   * Send the acceptances the dialog closed with, and bring the gate back if they do not land.
   * `ConsentService.acceptDocuments` has already restored what is owed by the time this catches,
   * so `present` has a gate to reopen.
   */
  private async record(acceptances: DocumentAcceptance[]): Promise<void> {
    try {
      await this.consent.acceptDocuments(acceptances);
    } catch (error) {
      console.error('Could not record the acceptance.', error);
      const stale = this.isStaleVersion(error);
      // Re-read puts the newly published text in front of the user: recording their tick against a
      // document they never saw would be recording consent they never gave.
      if (stale) await this.consent.load(true);
      this.snackBar.open(
        this.translate.instant(
          stale ? 'components.accept_legal.stale_version' : 'components.accept_legal.error'
        ),
        this.translate.instant('common.buttons.close'),
        { duration: stale ? 8000 : 5000 }
      );
      this.present();
    }
  }

  private isStaleVersion(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status === 409 &&
      error.error?.code === ApiErrorCode.CONSENT_STALE_VERSION
    );
  }

  /**
   * Re-check at every navigation. Never unsubscribed, by intent: a root singleton lives as long as
   * the tab, so there is no consumer whose destruction should end the gate.
   */
  private watchNavigation(): void {
    if (this.watching) return;
    this.watching = true;
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.consent
          .whenResolved()
          .then(() => this.present())
          .catch(error => console.error('Could not re-check pending legal documents.', error));
      });
  }
}
